'use client'

import { t } from '@lingui/core/macro'
import { Box, Typography, TextField, Select, MenuItem, FormControl } from '@mui/material'
import { FormField as FormFieldType } from '@/core/graphql/types'

const INPUT_SX = {
  borderRadius: 3,
  bgcolor: '#f3f4f5',
  color: '#191c1d',
  '& fieldset': { borderColor: 'rgba(204, 195, 215, 0.3)' },
  '&:hover fieldset': { borderColor: 'rgba(123, 63, 228, 0.32)' },
  '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'rgba(123, 63, 228, 0.55)' },
  '& textarea::placeholder': { color: '#7b7486', opacity: 1 },
}

const SELECT_SX = {
  borderRadius: 3,
  bgcolor: '#f3f4f5',
  color: '#191c1d',
  fontWeight: 600,
  '& .MuiSelect-nativeInput': {
    display: 'none',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(204, 195, 215, 0.3)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(123, 63, 228, 0.32)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '2px solid', borderColor: 'rgba(123, 63, 228, 0.55)' },
}

type EthicsFormFieldProps = {
  field: FormFieldType
  value: string | string[]
  error?: string
  onChange: (fieldId: string, value: string | string[]) => void
}

export function EthicsFormField({ field, value, error, onChange }: EthicsFormFieldProps) {
  const hasError = !!error

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          fontWeight: 800,
          letterSpacing: '0.14em',
          color: hasError ? 'error.main' : '#4a4454',
          mb: 1.5,
          ml: 0.5,
        }}
      >
        {field.label}
        {field.required ? (
          <Typography component="span" color="error.main" sx={{ ml: 0.5, fontWeight: 700 }}>
            *
          </Typography>
        ) : (
          <Typography component="span" sx={{ ml: 0.5, fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', fontSize: '0.75rem' }}>
            ({t`optional`})
          </Typography>
        )}
      </Typography>

      {field.type === 'Multiple' && field.options ? (
        <FormControl fullWidth error={hasError}>
          <Select
            value={Array.isArray(value) ? value[0] || '' : (value as string)}
            displayEmpty
            onChange={(e) => onChange(field.id, [e.target.value])}
            renderValue={(selected) => (selected ? (selected as string) : '--')}
            sx={SELECT_SX}
          >
            <MenuItem value="" disabled>
              --
            </MenuItem>
            {field.options.map((opt: string) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {hasError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
              {error}
            </Typography>
          )}
        </FormControl>
      ) : (
        <TextField
          fullWidth
          multiline={field.type === 'TextArea'}
          rows={field.type === 'TextArea' ? (field.id === 'suggestion' ? 3 : 5) : undefined}
          placeholder={field.label}
          value={value as string}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={hasError}
          helperText={hasError ? error : undefined}
          sx={{ '& .MuiOutlinedInput-root': INPUT_SX }}
        />
      )}
    </Box>
  )
}
